import styled, { css } from "styled-components";
import { Container, Row } from "@viagogo/core/ui";

export const BodyWrapper = styled.div<{
  $secondaryBgColor?: boolean;
  addPrimaryBgColor?: boolean;
  $overlapsHero?: boolean | undefined;
}>`
  width: 100%;
  height: 100%;
  background-color: ${(props) => {
    const backgroundColor = props.addPrimaryBgColor
      ? props.theme.colors.backgroundPrimary
      : props.theme.colors.backgroundSecondary;
    return props.$secondaryBgColor ? props.theme.palette.surfaceSecondary : backgroundColor;
  }};
  // Phone: card-style rounding and elevation, pulled up over the promo hero so the rounded corners
  // read against the image. Mirrors the venue page's body card (VenueLayoutV2/styles.ts).
  ${(props) =>
    props.$overlapsHero &&
    css`
      // The hero container is position: relative, so it paints above a static sibling. Position the
      // card too (with a stacking order) or the hero covers the lifted corners.
      position: relative;
      z-index: 1;
      border-start-start-radius: ${props.theme.spacing(6)};
      border-start-end-radius: ${props.theme.spacing(6)};
      box-shadow: 0px -2px 8px 1px rgba(0, 0, 0, 0.08);
      margin-block-start: ${props.theme.spacing(-6)};

      // CRH-762 promo variants: white body on the phone window, matching the venue page (it was grey).
      // The window is Broadway's 48rem boundary (the promo card hides from tablet up), not core's
      // 600px phone breakpoint — otherwise 600–767px shows the card on a grey body and the card's own
      // grey background vanishes.
      @media (max-width: 47.999rem) {
        background-color: ${props.theme.colors.backgroundPrimary};
      }

      @media (min-width: 48rem) {
        border-start-start-radius: 0;
        border-start-end-radius: 0;
        box-shadow: none;
        margin-block-start: 0;
      }
    `}
`;

// Overlapping the hero, the card opens with the venue page's primitives.300 (24px) of padding
// instead of the default spacing(2.25) (9px). The first section's own margin is flushed in
// LayoutBody so the two don't stack into a band of white space.
export const PaddedContainer = styled(Container)<{ $overlapsHero?: boolean | undefined }>`
  padding-top: ${(props) => (props.$overlapsHero ? props.theme.spacing(6) : props.theme.spacing(2.25))};
`;

export const FlushContainer = styled(Container)`
  padding-top: 0;
`;

// CRH-762 promo variants only: the filter row's own top margin is zeroed on phone
// (EventTypeSelectorAndFilterChips passes $removeTopMargin), so the promo layout re-adds a 12px gap
// between the Top Performers section and the filters. The 48rem boundary matches the promo card and
// the hero (Broadway's tablet condition), not core's 600px phone breakpoint.
export const PromoFilterSpacing = styled.div<{ $promo?: boolean | undefined }>`
  ${(props) =>
    props.$promo &&
    css`
      @media (max-width: 47.999rem) {
        margin-top: ${props.theme.spacing(3)};
      }
    `}
`;

export const VerticalMarginRow = styled(Row)`
  margin-top: ${(props) => props.theme.spacing(36)};
  margin-bottom: ${(props) => props.theme.spacing(18)};
`;

export const MarginDivLarge = styled.div<{
  $flushStart?: boolean | undefined;
  // Promo variant: the section below (featured show) ends here. The next section's heading should sit
  // closer to its own tiles than to these, so this gap is opened up (see CategoryGrid's promo header).
  $tightEnd?: boolean | undefined;
  // Promo variant: drops the section end margin entirely so the filter pills sit directly beneath
  // this section, mirroring the venue page (which adds no margin above its filter pills).
  $flushEnd?: boolean | undefined;
}>`
  margin: ${(props) => props.theme.spacing(8)} 0;
  ${(props) =>
    props.$flushStart &&
    css`
      margin-block-start: 0;
    `}
  ${(props) =>
    props.$tightEnd &&
    css`
      margin-block-end: ${props.theme.spacing(5)};
    `}
  ${(props) =>
    props.$flushEnd &&
    css`
      margin-block-end: 0;
    `}
`;
